import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import { selectCurrentDevice, selectCurrentDeviceFeatures, selectCurrentDeviceStatus, selectDeviceSpecialDayInfo, selectDeviceSpecialDayModify, selectDeviceSpecialDays, selectPasswordValid } from 'src/app/store/device/device.selector';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LIST_SPECIAL_DAY_PAGE_DEFAULT_SIZE } from 'src/app/conf/timers';
import { Device } from 'src/app/model/device';
import { DeviceStatus, isAutomFwUpdateInProgress, isDeviceUnavailable } from 'src/app/model/device-status';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { ConfirmationService } from 'primeng/api';
import { DeviceSpecialDayDialogComponent } from '../device-special-day-dialog/device-special-day-dialog.component';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { DeviceSpecialDays, DeviceSpecialDaysInfo } from 'src/app/model/device-timer';
import { DeviceFeature } from 'src/app/model/device-feature';
import { selectIsImpersMode, selectRoleExplainedTechnician } from 'src/app/store/user/user.selector';
import { PasswordDialogComponent } from '../password-dialog/password-dialog.component';

@Component({
	selector: 'app-timers-list-special-day',
	templateUrl: './timers-list-special-day.component.html',
	providers: [ConfirmationService, DialogService]
})

export class TimersListSpecialDayComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	specialDaysInfo$: Observable<DeviceSpecialDaysInfo | undefined>;
	specialDaysInfoSub: Subscription | undefined;
	specialDays$: Observable<DeviceSpecialDays[] | undefined>;
	specialDaysSub: Subscription | undefined;
	specialDayModify$: Observable<boolean | undefined>;
	specialDayModifySub: Subscription | undefined;

	deviceFeature$: Observable<DeviceFeature[] | undefined>;
	deviceFeatureSub: Subscription | undefined;
	passwordVerify$: Observable<boolean | undefined>;
	passwordVerifySub: Subscription | undefined;
	isTechnician$: Observable<boolean | undefined>;
	isTechnicianSub: Subscription | undefined;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	passwordSupport: boolean;
	passwordVerify: boolean;
	isTechnician: boolean;
	isImpersMode: boolean;

	loading: boolean;
	online: boolean;
	deviceStatus: DeviceStatus;

	specialDays: DeviceSpecialDays[];
	device: Device = new Device();
	specialDaysInfo: DeviceSpecialDaysInfo;
	opened = false;
	totalRecords = 0;
	specialDayModify: boolean;
	pageSize = LIST_SPECIAL_DAY_PAGE_DEFAULT_SIZE;


	showDiv = false;
	ref: DynamicDialogRef | undefined;
	callRest = false;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	memorySize: number;
	maxMemorySize: number;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private fb: FormBuilder,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.specialDaysInfo$ = this.store.select(selectDeviceSpecialDayInfo);
		this.specialDays$ = this.store.select(selectDeviceSpecialDays);
		this.specialDayModify$ = this.store.select(selectDeviceSpecialDayModify);
		this.deviceFeature$ = this.store.select(selectCurrentDeviceFeatures);
		this.passwordVerify$ = this.store.select(selectPasswordValid);
		this.isTechnician$ = this.store.select(selectRoleExplainedTechnician);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	ngOnInit(): void {
		this.callRest = false;
		this.getSpecialDaysInfo();
		this.deviceSub = this.device$.subscribe(currentDevice => {
			this.device = currentDevice;
		});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			if (deviceStatus !== undefined) {
				const wasAvailable = this.online;
				const nowAvailable = !isDeviceUnavailable(deviceStatus);
				if (nowAvailable && !wasAvailable) {
					this.getSpecialDaysInfo();
				}
				this.deviceStatus = deviceStatus;
				this.online = nowAvailable;
			}
		});
		this.specialDaysInfoSub = this.specialDaysInfo$.subscribe(specialDaysInfo => {
			this.specialDaysInfo = specialDaysInfo;
			if (specialDaysInfo !== undefined) {
				this.memorySize = specialDaysInfo.Used;
				this.maxMemorySize = specialDaysInfo.Max;
				this.loadSpecialDays();
			}
		});
		this.specialDaysSub = this.specialDays$.subscribe(specialDays => {
			if (specialDays !== undefined && specialDays.length > 0) {
				this.specialDays = this.specialDays.concat(specialDays);
				this.totalRecords = this.specialDays.length;
				if (this.specialDays.length < this.memorySize) {
					this.getListSpecialDays(this.specialDays[this.specialDays.length - 1].Position + 1);
				} else {
					this.loading = false;
					this.callRest = false;
				}
			}
		});
		this.specialDayModifySub = this.specialDayModify$.subscribe(
			specialDayModify => {
				this.specialDayModify = specialDayModify;
				if (this.specialDayModify) {
					this.getSpecialDaysInfo();
				}
			}
		);
		this.deviceFeatureSub = this.deviceFeature$.subscribe(deviceFeatures => {
			const fea = deviceFeatures.find(fe => fe.Name === 'password');
			if (fea !== undefined) {
				this.passwordSupport = true;
			}
		});
		this.passwordVerifySub = this.passwordVerify$.subscribe(passwordVerify => {
			this.passwordVerify = passwordVerify;
			if (this.passwordVerify) {
				this.loadSpecialDays();
			}
		});
		this.isTechnicianSub = this.isTechnician$.subscribe((isTechnician => {
			this.isTechnician = isTechnician;
		}));
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.onResize();
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.deviceFeatureSub?.unsubscribe();
		this.passwordVerifySub?.unsubscribe();
		this.isTechnicianSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		if (this.opened && this.online) {
			this.getSpecialDaysInfo();
			this.loadSpecialDays();
		}
	}

	loadSpecialDays(): void {
		if (this.opened && this.online) {
			if (!this.passwordSupport || this.passwordVerify || (!this.isTechnician && !this.isImpersMode)) {
				this.loadSpecialDaysRequest();
			} else {
				/* Open Dialog */
				this.ref = this.dialogService.open(PasswordDialogComponent, {
					header: this.translate.instant('PASSWORD'),
					width: '70vw',
					breakpoints: { '1600px': '98vw' },
					contentStyle: { overflow: 'auto' },
				});
			}
		}
	}

	getSpecialDaysInfo(): void {
		if (this.opened && this.online) {
			this.store.dispatch(DeviceActions.getDeviceSpecialDaysInfo());
		}
	}

	loadSpecialDaysRequest(): void {
		if (this.memorySize > 0) {
			this.specialDays = [];
			this.loading = true;
			if (!this.callRest) {
				this.callRest = true;
				this.getListSpecialDays(0);
			}
		} else {
			this.loading = false;
			this.specialDays = [];
		}
	}

	getListSpecialDays(from: number): void {
		this.store.dispatch(DeviceActions.getDeviceSpecialDays({ from: from }));
	}

	addSpecialDay(): void {
		this.store.dispatch(DeviceActions.resetDeviceSpecialDayForm());
		this.ref = this.dialogService.open(DeviceSpecialDayDialogComponent, {
			header: this.translate.instant('ADD_SPECIAL_DAY'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editSpecialDay(specialDay: DeviceSpecialDays): void {
		this.store.dispatch(DeviceActions.resetDeviceSpecialDayForm());
		this.ref = this.dialogService.open(DeviceSpecialDayDialogComponent, {
			header: this.translate.instant('EDIT_SPECIAL_DAY'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: { specialDay },
		});
	}

	deleteSpecialDay(specialDay: DeviceSpecialDays): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_SPECIAL_DAY'),
			message: this.translate.instant('DELETE_SPECIAL_DAY_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteDeviceSpecialDay({ position: specialDay.Position }));
			},
			reject: () => {
			}
		});
	}

	removeAll(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_SPECIAL_DAYS'),
			message: this.translate.instant('DELETE_SPECIAL_DAYS_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteDeviceAllSpecialDay());
			},
			reject: () => {
			}
		});
	}

	getValueMemory(): number {
		return Math.ceil((this.memorySize / this.maxMemorySize) * 100);
	}


	getDay(specialDay: DeviceSpecialDays): Date {
		let strRes = '';
		strRes = '20' + specialDay.Year + '-' + specialDay.Month + '-' + specialDay.Day;
		return new Date(strRes);
	}

	getMod(specialDay: DeviceSpecialDays): string {

		return specialDay.Mode.toString();
	}

	getTimeStart(specialDay: DeviceSpecialDays): string {
		if (specialDay.Mode !== 2) {
			return '';
		}
		if (specialDay.StartMin.toString().length === 1) {
			return specialDay.StartHour + ':0' + specialDay.StartMin;
		}
		return specialDay.StartHour + ':' + specialDay.StartMin;
	}

	getTimeEnd(specialDay: DeviceSpecialDays): string {
		if (specialDay.Mode !== 2) {
			return '';
		}
		if (specialDay.EndMin.toString().length === 1) {
			return specialDay.EndHour + ':0' + specialDay.EndMin;
		}
		return specialDay.EndHour + ':' + specialDay.EndMin;
	}
	getModTxt(specialDay: DeviceSpecialDays): string {
		switch (specialDay.Mode) {
			case 0: {
				return this.translate.instant('DISABLED');
			}
			case 1: {
				return this.translate.instant('ENABLE');
			}
			case 2: {
				return this.translate.instant('CUSTOM');
			}
			default: {
				return '';
			}
		}
	}

	getModColor(specialDay: DeviceSpecialDays): string {
		switch (specialDay.Mode) {
			case 0: {
				return 'w-10rem p-tag-lightBlue0220';
			}
			case 1: {
				return 'w-10rem p-tag-lightBlue0240';
			}
			case 2: {
				return 'w-10rem p-tag-lightBlue0260';
			}
			default: {
				return '';
			}
		}
	}

	isAccess(): boolean {
		if (!this.passwordSupport) {
			return true;
		}
		if (this.passwordVerify) {
			return true;
		}
		if (!this.isTechnician && !this.isImpersMode) {
			return true;
		}
		return false;
	}

	isAutomFwUpdateInProgress(): boolean {
		return isAutomFwUpdateInProgress(this.deviceStatus);
	}

	unavailableAlertKey(): string {
		return this.isAutomFwUpdateInProgress() ? 'DEVICE_FW_UPDATE_IN_PROGRESS_ALERT' : 'OFFLINE_ALERT';
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}
}
